import ObjectId from 'bson-objectid';
import { addScheduledMessageButtonActions } from "@common-reducers/AddScheduledMessageButtonReducer";
import store, { dispatch as typedDispatch } from "@extension-context-common/store/extension-store";
// import { updateAckInMessageLogs } from "@common-reducers/CampaignThunks";
import { setWhatsappInitialized } from '@common-reducers/ConnectionReadinessSlice';
import { updateProfilePic } from "@common-reducers/ProfilePicReducer";
import { taskActions } from "@common-reducers/TasksReducer";
import { setCurrentExtensionVersion, setInvalidWANumber, setIsReply, setMe, setUserInfo } from "@common-reducers/WhatsAppReducer";
import { externalAccessToken, initAuth } from "@/common/bt-auth/AuthThunks";
import { AuthBridgeMessage } from "@bt-common/interfaces/auth-bridge-contracts.interface";
import { logout } from "@common-reducers/UserReducer";
import { isSyncLoginLogoutEnable } from "@common/config/constants";
import { BLUETICKS_TOKEN_KEY } from "@common/reusable/common";
import { updateUserPhoneNumberThunk, subscribeToFCMTopicThunk } from "@common/reducers/UserThunks";
import { MessageInterface, SendResponseFunction } from "@common/services/messageService";
import { PostMessage } from "@bt-common/interfaces/post-message.interface";
import { handleNewIncomingWaMessageThunk, handlePollVoteWebhookThunk, handleMessageDeletedRevokedWebhookThunk, setCurrentChatThunk, handleVaultHistoricalSyncThunk } from "@extension-context-content/thunks/WhatsappThunks";
import type { WaListenerEventEnvelope } from "@bt-common/wa-events/wa-event-relevance";
import { ProfilePicThumbEvent } from "@bt-common/interfaces/whatsapp-types";
import { dispatchWaEventThunk } from '../thunks/WaEventDispatchThunk';
import { reportEngineConnectedOnceThunk } from '../thunks/report-engine-connected';
import { transcribeMessageThunk } from '../thunks/TranscribeMessageThunks';
import { isAiLinkedChat } from './ai-linked-chats-registry';
import {
  isVoiceNoteMessageType,
  markAiLinkedChatEnvelope,
  resolveAiLinkedChatId,
  shouldIngestAiLinkedChatMessage,
} from './ai-linked-chat-ingest';
import { initSendMessagesInterval, onWapiInitialized } from "./schedule-service";
import { takePendingAck } from "../pending-ack-registry";
import { promotePendingAck, reportIncomingNotRendered } from "@extension/wa-communication-layer/api/api-calls";
import { getSerialized } from "@extension/wa-communication-layer/core/utils/wa-key-serialization";
import { postMessageWithResponse } from "@common-services/post-message-to-whatsapp-functions";
import { diagTotalMs, loadAssetBlob, SLOW_FETCH_WARN_MS } from "@extension/wa-communication-layer/message/fetch-asset-blob";
import { WA_TOOL_COMMANDS } from "@bt-common/interfaces/whatsapp-tools-commands.interface";
import { addAiConversationMarker } from "@bt-common/wa-events/ai-conversation-marker";
import { buildFileReceivedTurnText } from "@common/services/file-intel-helpers";
import { selectBtgptFilesAutoRecognition } from "@common-reducers/UserSelectors";
import { captureMessage as vaultCaptureMessage, buildVaultRowFromWaMessage } from "@common/services/message-vault";
import { isVaultCaptureEnabled } from "@extension/services/vault-backup/backup-config-selectors";


/**
 * Page-context can't fetch `http://localhost:*` URLs from `web.whatsapp.com`
 * (HTTPS) due to browser mixed-content policy, and can't fetch arbitrary
 * cross-origin URLs without CORS. Content-script runs in the extension
 * realm where neither restriction applies — manifest `host_permissions`
 * grant access. Page-handler `prepareMessageAttachment` posts the URL
 * here; we fetch the bytes + return base64. Page reconstitutes the Blob
 * (BE#104).
 */
export const handleFetchAssetBlob = (
  message: { url?: unknown },
  sendResponse: SendResponseFunction,
): void => {
  const url = typeof message?.url === 'string' ? message.url : '';
  if (!url) {
    sendResponse?.({ success: false, error: 'url is required' });
    return;
  }
  void loadAssetBlob(url).then((result) => {
    if (!result.success || (result.diag && diagTotalMs(result.diag) > SLOW_FETCH_WARN_MS)) {
      // diag carries the host only; never log the URL (can be signed).
      console.warn('[fetchAssetBlob][content]', result.diag, result.success ? '' : result.error);
    }
    sendResponse?.(result);
  });
};

export const handleCurrentExtensionVersion = (event: { currentVersion?: string }) => {
  console.log('[login-flow] [context:content] Current version:', event?.currentVersion);
  store.dispatch(setCurrentExtensionVersion(event?.currentVersion ?? ""));
};

export const handleAccessToken = (accessToken: string) => {
  console.log('[login-flow] [context:content] handleAccessToken', !!accessToken);
  store.dispatch(externalAccessToken(accessToken));
};

// Page->background login relay. The web app (page/main world) has chrome.runtime
// via externally_connectable but chrome.runtime.id is undefined, so it can't
// message the background directly. AuthService.persistToken posts a `set-token`
// window message instead; the co-injected content script (this MessageService)
// emits it locally -> here we forward it to the background (content scripts DO
// have chrome.runtime.id) so handleSetToken populates the chrome.storage hub and
// the popup auto-logs-in. Counterpart to the `clear-local-token` logout fan-out.
// Guard on chrome.runtime.id so only the content-script context forwards (never
// the page itself) and the background never re-broadcasts set-token -> no loop.
export const handleSetTokenRelay = (event: AuthBridgeMessage) => {
  if (!isSyncLoginLogoutEnable) return;
  try {
    const accessToken = event?.data?.accessToken ?? event?.accessToken;
    const canMessageBackground =
      typeof chrome !== 'undefined' && !!chrome.runtime?.id && !!chrome.runtime?.sendMessage;
    if (accessToken && canMessageBackground) {
      console.log('[login-flow] [context:content] relaying set-token to background');
      chrome.runtime.sendMessage({ cmd: 'set-token', data: { accessToken } });
    }
  } catch (e) {
    console.log('[login-flow] [context:content] handleSetTokenRelay failed', e);
  }
};

// Page->background LOGOUT relay (counterpart to handleSetTokenRelay). The web app
// page world can't message the background (no chrome.runtime.id), so
// AuthService.clearStoredToken posts a `web-logout` window message; the co-injected
// content script emits it locally -> here we forward a `logout` to the background
// (content scripts DO have chrome.runtime.id) so handleLogout clears the
// chrome.storage hub + broadcasts clear-local-token -> the popup logs out.
// Distinct inbound cmd (`web-logout`) avoids the wa-state-manager `logout` handler;
// guarded on chrome.runtime.id so only the content script forwards (no loop).
export const handleWebLogoutRelay = () => {
  if (!isSyncLoginLogoutEnable) return;
  try {
    const canMessageBackground =
      typeof chrome !== 'undefined' && !!chrome.runtime?.id && !!chrome.runtime?.sendMessage;
    if (canMessageBackground) {
      console.log('[login-flow] [context:content] relaying web-logout -> logout to background');
      chrome.runtime.sendMessage({ cmd: 'logout' });
    }
  } catch (e) {
    console.log('[login-flow] [context:content] handleWebLogoutRelay failed', e);
  }
};

// Broadcast from the background on logout. Clears THIS origin's token so the WA
// content script can't re-bridge it to chrome.storage, and clears Redux via the
// pure `logout()` reducer (NOT logoutThunk) so it does not re-broadcast -> no loop.
export const handleClearLocalToken = () => {
  if (!isSyncLoginLogoutEnable) return;
  console.log('[login-flow] [context:content] handleClearLocalToken: clearing local bt-token');
  try { localStorage.removeItem(BLUETICKS_TOKEN_KEY); } catch (e) { /* ignore */ }
  store.dispatch(logout());
  // Relay logout to the page world so the web-app page-world React logs out too. This is the
  // missing counterpart to handleSetLocalToken's `bt-extension-login` relay — without it an
  // extension logout cleared localStorage but left the page-world app logged in until reload.
  try {
    if (typeof window !== 'undefined') {
      window.postMessage({ cmd: 'bt-extension-logout' }, window.location.origin);
    }
  } catch (e) { /* ignore */ }
};

// Login fan-out broadcast from the background (mirror of handleClearLocalToken). Writes
// THIS origin's token to localStorage, logs the content-script store in (initAuth reads
// the hub + authenticates — guarded against loops via lastAttemptedJwtToken), and relays
// the token to the page world so the web-app page-world React logs in too. The relay uses
// a DISTINCT cmd `bt-extension-login` (NOT `set-token`, which is the page->content
// direction) so the page listener can't collide with its own outbound set-token.
export const handleSetLocalToken = (event: AuthBridgeMessage) => {
  if (!isSyncLoginLogoutEnable) return;
  const accessToken = event?.data?.accessToken ?? event?.accessToken;
  console.log('[login-flow] [context:content] handleSetLocalToken', { hasToken: !!accessToken });
  if (!accessToken) return;
  try { localStorage.setItem(BLUETICKS_TOKEN_KEY, accessToken); } catch (e) { /* ignore */ }
  store.dispatch(initAuth());
  try {
    if (typeof window !== 'undefined') {
      window.postMessage({ cmd: 'bt-extension-login', data: { accessToken } }, window.location.origin);
    }
  } catch (e) { /* ignore */ }
};

/**
 * Forward TRANSCRIBE_PROGRESS payloads from the offscreen document onto a
 * DOM CustomEvent so the in-page transcription modal (in
 * reminders-injection.tsx) can update without depending on chrome.runtime
 * directly. The offscreen → background → content-script → DOM event chain
 * keeps modal UI logic isolated from extension-message plumbing.
 */
export const handleTranscribeProgress = (data: any) => {
  try {
    document.dispatchEvent(new CustomEvent('bt:transcribe-progress', { detail: data }));
  } catch (e) {
    console.warn('[transcribeProgress] dispatch failed', e);
  }
};

export const handleUserInfo = (userInfoMessage: PostMessage) => {
  store.dispatch(setUserInfo({ email: "", id: "" }));
};

// New handlers from whatsapp.service
export const handleWapiInitialized = (message: PostMessage, sendResponse: SendResponseFunction) => {
  console.log('[login-flow] [context:content] wapi-initialized');
  typedDispatch(onWapiInitialized());
  store.dispatch(updateUserPhoneNumberThunk());

  // Mark WhatsApp as initialized and try to initialize connection
  store.dispatch(setWhatsappInitialized(true));
  console.log('[tryInitializeConnection] WAPI Initialized');
  // store.dispatch(tryInitializeConnection());

  sendResponse?.({});
};



export const handleCurrentChat = (chat: any, sendResponse: SendResponseFunction) => {
  typedDispatch(setCurrentChatThunk({
    chat
  }));
  store.dispatch(taskActions.setIsTasksPanelOpen(false));
  sendResponse?.({})
};

// WhatsApp Web fires own-message events even before the Blueticks user logs in.
// Posting wa-message-listener-event without an access token gets rejected by the
// backend as "Not authenticated" (backend-api's prepareHeaders only attaches the
// bearer when an accessToken is present). Skip the dispatch when logged out.
const isLoggedIn = (): boolean => !!store.getState()?.UserReducer?.accessToken;

// Own sent message (fromMe) — forward to the live-listener pipeline ONLY.
// No webhook dispatch, no chatbot trigger: those are inbound-message
// contracts; this exists so the dashboard's Team Inbox sees sends made on
// WhatsApp Web / the phone in real time.
const ingestOwnMessage = (message: any, contactDisplayName?: string) => {
  if (!isLoggedIn()) return;
  // ── Message Vault: capture OUTGOING message on send ──────────────
  // Mirrors the incoming capture (handleNewIncomingWaMessageThunk) so
  // the backup covers both directions. Gated on the vault master switch;
  // fire-and-forget + idempotent on msgId. Voice-note / thumbnail byte
  // enrichment is incoming-only — outgoing captures the synchronous row.
  try {
    if (isVaultCaptureEnabled(store.getState() as any)) {
      const row = buildVaultRowFromWaMessage(message);
      if (row) void vaultCaptureMessage(row);
    }
  } catch (err) {
    console.warn('[bt-vault] own-message capture failed:', err);
  }
  try {
    // btgpt "AI Conversation" bridge: a fromMe message in a linked group is
    // USER INPUT for a headless btgpt turn (also covers input typed on the
    // phone). It must pierce the fromMe gate that normally routes own messages
    // to Team Inbox only. Marked/echo replies (hidden marker) and non-linked
    // chats fall through to the identical behaviour as before this feature.
    const chatId = resolveAiLinkedChatId(message);
    const body = typeof message?.body === 'string' ? message.body : '';
    if (
      shouldIngestAiLinkedChatMessage({
        fromMe: message?.fromMe !== false,
        isLinkedChat: isAiLinkedChat(chatId),
        body,
      })
    ) {
      // Async branch: voice notes are transcribed pre-dispatch. Fire-and-forget
      // so the message pump stays non-blocking for every OTHER own message —
      // only linked-chat messages take the async path.
      void dispatchAiLinkedOwnMessage(message);
      return;
    }
    store.dispatch(dispatchWaEventThunk({ type: 'receivedNewMessage', msg: message, contactDisplayName }) as any);
  } catch (error) {
    console.error("Error forwarding own message listener event:", error);
  }
};

/** Whisper transcription can stall on long clips; cap it so a stuck
 *  transcription can never wedge the AI-conversation bridge. Generous because
 *  the FIRST transcription of a session pays the Whisper model cold-start
 *  (download + init), which alone can exceed 45s (observed live: a 4s clip
 *  timed out at 45s on a cold model). */
const AI_TRANSCRIBE_TIMEOUT_MS = 150_000;

/** Race the transcribe thunk against a timeout. Returns the transcript text, or
 *  undefined on failure/empty/timeout (caller drops the event — no reply is
 *  better than a wrong one). */
/** A freshly-recorded OUTGOING voice note (the linked-AI-group case) hasn't
 *  finished WhatsApp's media PREPARING/upload stage at the instant
 *  the own-message branch fires, so the first media fetch comes back empty in a
 *  few ms — long before Whisper could ever run. Retrying until the bytes are
 *  ready (received notes resolve on the first try) is the difference between a
 *  real transcript and an instant false "couldn't transcribe" notice.
 *
 *  We retry ONLY the "media not ready" outcome — a null result with NO
 *  structured `transcription` object (nothing was decoded). Once Whisper has
 *  actually run, the result carries a `transcription` (even for genuine
 *  silence), and we stop: retrying can't turn silence into words. */
const AI_TRANSCRIBE_MEDIA_MAX_WAIT_MS = 20_000;
const AI_TRANSCRIBE_RETRY_DELAY_MS = 800;

const transcribeAiVoiceNote = async (messageKey?: string): Promise<string | undefined> => {
  if (!messageKey) return undefined;
  const deadline = Date.now() + AI_TRANSCRIBE_MEDIA_MAX_WAIT_MS;
  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    attempt++;
    try {
      const result = await Promise.race([
        store.dispatch(transcribeMessageThunk({ messageKey })).unwrap(),
        new Promise<{ text: null; transcription: null }>((resolve) =>
          setTimeout(() => resolve({ text: null, transcription: null }), AI_TRANSCRIBE_TIMEOUT_MS),
        ),
      ]);
      const text = (result as { text?: string | null })?.text;
      if (text) return text;
      // Whisper actually ran (structured result present) but produced nothing —
      // genuine silence/failure. Retrying won't help; stop here.
      const decoded = (result as { transcription?: unknown })?.transcription;
      if (decoded) return undefined;
      // Media not ready yet (empty, nothing decoded). Wait and retry until the
      // recording finishes preparing, then give up.
      if (Date.now() >= deadline) {
        console.warn('[ai-linked-chat] voice media never became ready — giving up', { messageKey, attempt });
        return undefined;
      }
      console.log(`[ai-linked-chat] voice media not ready (attempt ${attempt}) — retrying in ${AI_TRANSCRIBE_RETRY_DELAY_MS}ms`, messageKey);
      await new Promise((r) => setTimeout(r, AI_TRANSCRIBE_RETRY_DELAY_MS));
    } catch (err) {
      // A throw can also be transient (message not yet queryable in the store
      // right after send) — retry until the deadline, then surface failure.
      console.warn('[ai-linked-chat] transcribe threw', err);
      if (Date.now() >= deadline) return undefined;
      await new Promise((r) => setTimeout(r, AI_TRANSCRIBE_RETRY_DELAY_MS));
    }
  }
};

/** Send a plain text message into a chat via the page-context send tool. Used
 *  by the voice-note flow below; the minimal UserMessage shape is what
 *  prepareMessageForWa needs (contactList[0].id = chat, message = text).
 *
 *  `quotedMessageId` (optional) makes the message a REPLY to that message —
 *  the send thunk quotes it (WhatsappSendMessageThunk reads
 *  userMessage.quotedMessageId). Used to attach a voice-note transcript to the
 *  original audio bubble so the user sees what was understood in context. */
const sendAiBridgeChatMessage = async (
  chatId: string,
  text: string,
  quotedMessageId?: string,
): Promise<void> => {
  await postMessageWithResponse(
    WA_TOOL_COMMANDS.sendMessageThunkTool,
    {
      userMessage: {
        // rev 3.3 §6.6: a real 24-hex ObjectId (it is the messageSecret token;
        // the old `btai_<ms>_<rand>` was 25 bytes of non-platform id).
        _id: ObjectId().toHexString(),
        message: text,
        contactList: [{ id: chatId }],
        // BOTH fields, mirroring DiagnosticsToolRunner: the page-side send path
        // derives the quote from replyData._serialized (resolveQuotedMessageId
        // in message-utils.ts), NOT from quotedMessageId — quotedMessageId alone
        // rendered a plain, unquoted message (measured live).
        ...(quotedMessageId
          ? { quotedMessageId, replyData: { _serialized: quotedMessageId } }
          : {}),
      },
    },
    { timeoutOverride: 60_000 },
  );
};

/** Dispatch a linked-chat own message.
 *
 *  TEXT messages: marked `aiLinkedChat: true` and dispatched to the backend
 *  bridge as the turn input.
 *
 *  VOICE notes: the audio event NEVER goes to the server (nothing downstream
 *  can use audio). Instead the extension transcribes locally and SENDS the
 *  transcript back into the chat as a visible, UNMARKED message —
 *  `🎙️ "<text>"` — which then re-enters this same handler as a normal text
 *  message and becomes the turn input (single canonical input channel; the
 *  transcript lives in WA history and syncs to the phone). On transcription
 *  failure a MARKED notice is sent instead (visible to the user, never
 *  ingested as input).
 */
const dispatchAiLinkedOwnMessage = async (message: any): Promise<void> => {
  try {
    if (isVoiceNoteMessageType(message?.type)) {
      const chatId = resolveAiLinkedChatId(message);
      if (!chatId) return;
      // WhatsApp Web dropped the raw `_serialized` getter on MsgKey (build
      // 2026-07) — reading `message.id._serialized` now yields undefined, so the
      // transcribe call bailed instantly with "couldn't transcribe". Route
      // through getSerialized (reconstructs the key from fields). See wa-key-
      // serialization.ts + [[wa-msgkey-serialized-undefined]].
      const voiceKey = getSerialized(message?.id);
      const transcribedText = await transcribeAiVoiceNote(voiceKey);
      if (!transcribedText) {
        console.warn(
          '[ai-linked-chat] voice-note transcription failed/empty — sending in-chat notice',
          voiceKey,
        );
        await sendAiBridgeChatMessage(
          chatId,
          addAiConversationMarker('🤖 לא הצלחתי לתמלל את ההקלטה 🎙 — נסו שוב או כתבו לי.'),
        );
        return;
      }
      // Post the transcript as a REPLY quoting the original voice note, so the
      // user sees what was understood attached to the audio bubble. It stays
      // UNMARKED, so it re-enters this handler as the turn input as before —
      // now carrying reply-context to the voice note for the agent.
      await sendAiBridgeChatMessage(chatId, `🎙️ "${transcribedText}"`, voiceKey);
      return;
    }
    // Document / image messages: the bytes never go to the server here. Instead
    // the envelope becomes a TEXT turn carrying a `[file received] …` reference
    // (name + mimetype + message_key) — the backend resolves the media by
    // message_key when it decides to identify/ingest. Shallow-copy with the body
    // replaced and type forced to 'chat' so backend processMessage keeps the
    // body verbatim (a 'document'/'image' type would be treated as media, not
    // input). Video/sticker fall through to the unchanged text branch below.
    // File message (document OR image): detect broadly — WA reports the type as
    // 'document'/'image' but the mimetype is the reliable signal (a photo can
    // arrive with a bare type but a real image/* mime; a PDF as application/pdf).
    const mimetype = typeof message?.mimetype === 'string' ? message.mimetype : '';
    const isFileMessage =
      message?.type === 'document' ||
      message?.type === 'image' ||
      /^image\//i.test(mimetype) ||
      /^application\/(pdf|msword|vnd\.)/i.test(mimetype);
    if (isFileMessage) {
      const filename = typeof message?.filename === 'string' ? message.filename : undefined;
      // MsgKey._serialized is undefined on the new WA build — reconstruct the
      // key so the `[file received] … message_key=` reference the backend
      // resolves by isn't empty. See [[wa-msgkey-serialized-undefined]].
      const messageKey = getSerialized(message?.id);
      // "Files auto-recognition" permission (btgpt Permissions popover, stored
      // on the USER DOC so it syncs here over MQTT):
      //   ON  → forward `[file received]` to the agent, which identifies the
      //         file itself (Claude vision via its files tool): invoice →
      //         auto-file to the Invoices board + confirm; anything else →
      //         reply on the file asking what to do.
      //   OFF → drop the event entirely — nothing happens until the user
      //         REPLIES on the file ("check this") or asks; the reply-context
      //         line the text branch appends carries the file's message_key.
      const autoRecognition = selectBtgptFilesAutoRecognition(store.getState() as any);
      if (!autoRecognition) {
        console.log('[ai-linked-chat] file message DROPPED (files auto-recognition OFF)', { key: messageKey, mimetype });
        return;
      }
      const body = buildFileReceivedTurnText({ filename, mimetype, messageKey });
      console.log('[ai-linked-chat] file message → [file received] (agent identifies)', { type: message?.type, mimetype, key: messageKey });
      const enrichedFile = markAiLinkedChatEnvelope({ ...message, body, type: 'chat' });
      store.dispatch(dispatchWaEventThunk({ type: 'receivedNewMessage', msg: enrichedFile }) as any);
      return;
    }
    // TEXT message. When it REPLIES to another message (quotedStanzaID), append
    // a reply-context line so the agent can act on the quoted message — this is
    // what makes the auto-recognition-OFF flow work: the user replies on a file
    // with "check this / do X with this" and the agent gets the file's
    // message_key to pass to its files tool.
    const quotedStanza =
      (message as { quotedStanzaID?: unknown })?.quotedStanzaID ??
      (message as { _data?: { quotedStanzaID?: unknown } })?._data?.quotedStanzaID;
    let textMessage = message;
    if (typeof quotedStanza === 'string' && quotedStanza && typeof message?.body === 'string' && message.body.trim()) {
      textMessage = { ...message, body: `${message.body}\n[reply to message_key=${quotedStanza}]` };
      console.log('[ai-linked-chat] text reply → appended reply-context', { quotedStanza });
    }
    const enriched = markAiLinkedChatEnvelope(textMessage);
    store.dispatch(dispatchWaEventThunk({ type: 'receivedNewMessage', msg: enriched }) as any);
  } catch (err) {
    console.warn('[ai-linked-chat] linked own-message dispatch failed', err);
  }
};

/** Handler for the historical vault sync bridge.
 *  Fires once shortly after WA finishes its initial sync — carries a
 *  batch of plain-object message rows that should be upserted into
 *  the local vault. */
/** #44 telemetry: fire-and-forget relay of the page probe's count-only payload to the backend. */
export const handleIncomingNotRendered = (data: any, sendResponse: SendResponseFunction) => {
  reportIncomingNotRendered(data).catch(() => { /* telemetry must never surface */ });
  sendResponse?.({});
};

export const handleVaultHistoricalSync = (data: any, sendResponse: SendResponseFunction) => {
  store.dispatch(handleVaultHistoricalSyncThunk(data));
  sendResponse?.({});
};

/**
 * THE content-side receiver of every taxonomy event (Engine Wire Contract,
 * plan 3.2). The page's emitWaEvent already mapped the identity fields, so the
 * envelope is wire-form. Real per-type logic lives here for the types that
 * have it; every other type goes straight to dispatchWaEventThunk (relevance +
 * the one backend send, which itself skips when logged out).
 */
export const handleWaEvent = (envelope: WaListenerEventEnvelope, sendResponse: SendResponseFunction) => {
  sendResponse?.({});
  const { type, msg, contactDisplayName } = envelope ?? ({} as WaListenerEventEnvelope);
  const message = msg as any;
  try {
    switch (type) {
      case 'receivedNewMessage':
        // 1. incoming: vault, bot match, cancel-if-received, analytics.
        // 2. own: vault, AI-linked chat.
        if (message?.fromMe === true) ingestOwnMessage(message, contactDisplayName);
        else typedDispatch(handleNewIncomingWaMessageThunk({ message, contactDisplayName }));
        return;
      case 'receivedMessageAckUpdate': {
        // 3. Push-driven status upgrade (the event is fromMe-gated at the page
        // emit). If this is a message WE sent (present in the pending-ack
        // registry) and the ack reached SERVER (>= 1), tell the backend to
        // promote sent_pending_ack → confirmed. take() removes the mapping so
        // each message promotes at most once. Both the registry key (send
        // result) and this key are wire-form. Never throws into the router.
        try {
          const waMessageKey = message?.id?._serialized;
          if (typeof message?.ack === 'number' && message.ack >= 1 /* SERVER */ && typeof waMessageKey === 'string') {
            const messageId = takePendingAck(waMessageKey);
            if (messageId) void promotePendingAck(messageId, message.ack).catch(() => { /* best-effort */ });
          }
        } catch { /* never let an ack push break the handler */ }
        store.dispatch(dispatchWaEventThunk({ type, msg: message }) as any);
        return;
      }
      case 'poll_vote_webhook':
        // 4. webhook fan-out + the bot / AI-conversation bridge.
        typedDispatch(handlePollVoteWebhookThunk(message));
        return;
      case 'message_deleted_revoked_webhook':
        // Vault: record the deletion for the restore chip, then forward.
        store.dispatch(handleMessageDeletedRevokedWebhookThunk(message));
        return;
      default:
        store.dispatch(dispatchWaEventThunk({ type, msg: message }) as any);
    }
  } catch (error) {
    console.error('[wa-event] handler failed', type, error);
  }
};

export const handleMe = (event: any) => {
  store.dispatch(setMe(event.data.me));
  // #113 FE-1 — best-effort, once-per-page-load report that this extension
  // engine is connected (satisfies the first-week checklist's `connect` step
  // without a user-facing CTA — see report-engine-connected.ts for why).
  // typedDispatch (not store.dispatch) — matches handlePollVoteWebhookThunk
  // above, the other CommonRootState-typed thunk dispatched from this file.
  typedDispatch(reportEngineConnectedOnceThunk());
};

export const handleProfilePicThumb = (event: any) => {
  store.dispatch(updateProfilePic(event.data as ProfilePicThumbEvent));
};

export const handleInvalidWAPhoneNumber = (data: any) => {
  console.log('[handleInvalidWAPhoneNumber] Invalid WhatsApp number:', data);
  const phone = data?.phone;
  store.dispatch(setInvalidWANumber(phone));
};



export const handleCheckIfReplyingToMessageResponse = (event: any) => {
  const testData = event.data.data;
  if (testData) {
    store.dispatch(setIsReply(testData));
  }
};

// export const handleLogAckRecordByCampaignIdForNewMessageLogs = (event: any) => {
//   const logAckRecord = event.data.logAckRecord;
//   store.dispatch(updateAckInMessageLogs( logAckRecord ));
// };

export const handleGetPollDataResponse = (event: any) => {
  const poll = event.data.poll;
  if (poll) {
    store.dispatch(addScheduledMessageButtonActions.setPollFromWAModal(poll));
  }
};


// FCM Token handler
export const handleFCMTokenUpdate = async (token: string) => {
  console.log('[FCM] [context:content] FCM token received from background script:', { length: token.length });

  // Subscribe the FCM token to the user's personal topic
  try {
    const result = await store.dispatch(subscribeToFCMTopicThunk(token));
    const payload = result.payload as { success: boolean; topicName?: string; error?: string };

    if (payload?.success) {
      console.log('[FCM] [context:content] ✅ Successfully subscribed to FCM topic:', payload.topicName);
    } else {
      console.error('[FCM] [context:content] ❌ Failed to subscribe to FCM topic:', payload?.error);
    }
  } catch (error) {
    console.error('[FCM] [context:content] Error subscribing to FCM topic:', error);
  }
};
